import { DragEndEvent, DragStartEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ColoredImageIcon, useNotificationModal } from '@room-match/shared-ui-components';
import AsyncMediaGallery from 'components/AsyncMediaGallery/AsyncMediaGallery';
import AsyncPhotoEditorV2 from 'components/AsyncPhotoEditorV2/AsyncPhotoEditorV2';
import { isEqual } from 'lodash';
import { createRef, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import ProgressBar from 'shared/components/ProfileProgressBar/ProgressBar';
import { ROUTES } from 'shared/constants/ROUTES';
import StyledDialog from 'shared/elements/StyledDialog/StyledDialog'; // Use Talent app level folder StyledDialog - Jerome
import { useMediaShareableProfileFormat } from 'shared/hooks/useMediaShareableProfileFormat';
import useWindowDimensions from 'shared/hooks/useWindowDimenstion';
import IMedia from 'shared/interfaces/IMedia';
import {
  IAgencyTalentPermissionPayload,
  IOutput,
  IProfileGraphQL,
  IProfileMedia,
  IProfileMediaSetSelectPayload,
} from 'shared/interfaces/IProfile';
import { ISkillCategory } from 'shared/interfaces/ISkills';
import { mediaService } from 'shared/services/mediaService';
import { profileService } from 'shared/services/profileService';
import { talentService } from 'shared/services/talentService';
import { uploadService } from 'shared/services/uploadService';
import { imageURLtoFile, isJsonString } from 'shared/utils/common';
import { errorResponseToArray } from 'shared/utils/room-shared';
import { useDebounce } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';
import SkillsData from '../../../../../data/Skills.json';
import {
  AddButton,
  CompletionCont,
  Content,
  Count,
  Fetching,
  Header,
  HeaderTitle,
  Hidden,
  HiddenCont,
  HLeft,
  HRight,
  Name,
  Percent,
  SectionCont,
} from './image-video-audio.styles';
import ImageItem from './Items/ImageItem';
import UploadingItem from './Items/UploadingItem';
import AddModal from './Modals/AddModal';
import SortableImages, { SortableImagesRef } from './SortableImages';

interface Props {
  fetching: boolean;
  handleAgencyTalentPermissions: (payload: IAgencyTalentPermissionPayload) => void;
  permissions?: string[];
  nonRepre: boolean;
  profiles?: IProfileGraphQL[];
}

interface IOutputE extends IOutput {
  id: string;
}

const { patchMultipleMedia, patchMediaTags, uploadMedia } = uploadService();
const { getMediaProfile, setSelectProfileMedia, unSelectProfileMedia, updateProfileMediaSort, setProfilePrimaryImage } =
  profileService();
const { getMediaList, getMediaTags } = mediaService();
const { updateTalent, getSkills } = talentService();

function Images({ fetching, handleAgencyTalentPermissions, permissions, nonRepre, profiles }: Props) {
  const { mutateAsync: updateSkillAsync } = updateTalent();
  const { data: skillsData } = getSkills();
  const theme = useTheme();
  const [mediaPatching, setMediaPatching] = useState(false);
  const [queryValue] = useDebounce('', 500);
  const { data: mediaTags, isLoading: fetchingTags } = getMediaTags(queryValue);
  const history = useHistory();
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const { profileId } = useParams() as { profileId: string };
  const { data: mediaProfileData, refetch: refetchMediaProfileData } = getMediaProfile(profileId, {
    file_type: 'image',
  });
  const {
    data: mediaData,
    isLoading: isMediaLoading,
    refetch: refetchMediaData,
  } = getMediaList({ file_type: 'image', media_bin: true });
  const { mutate: primaryImageMutate } = setProfilePrimaryImage();
  const { mutateAsync: patchMultipleMediaAsync } = patchMultipleMedia();
  const { mutateAsync: patchMediaTagsAsync } = patchMediaTags();
  const { mutateAsync: uploadMediaMutateAsync } = uploadMedia();
  const { mutateAsync: setSelectProfileMediaMutateAsync } = setSelectProfileMedia();
  const { mutate: updateSortMutate } = updateProfileMediaSort();
  const [images, setimages] = useState<IProfileMedia[]>([]);
  const [imageIndex, setimageIndex] = useState(0);
  const [showImageModal, setshowImageModal] = useState(false);
  const [showEditor, setshowEditor] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [files, setfiles] = useState<File[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { mutate, mutateAsync } = setSelectProfileMedia();
  const { mutate: unselectMutate } = unSelectProfileMedia();
  const [expanded, setExpanded] = useState(true);
  const [toUpload, settoUpload] = useState<IOutputE[]>([]);
  const { width } = useWindowDimensions();
  const fileInput: any = createRef();
  const { handleOpen, handleSetMessage, handleChangeModalType } = useNotificationModal({
    type: 'error',
  });
  const [metaData, setmetaData] = useState<null | {}>(null);
  const [idToEdit, setIdToEdit] = useState('');

  // Add a ref for storing timers to clean up
  const timersRef = useRef<any>({});

  // Add this near the top of your component function
  const safeUpdateRef = useRef(false);

  // Add a utility function for safe state updates
  const safeUpdate = useCallback((updateFn: () => void) => {
    if (safeUpdateRef.current) return;

    // Set a flag to avoid multiple rapid updates
    safeUpdateRef.current = true;

    // Clear any pending updates
    if (timersRef.current.updateTimer) {
      clearTimeout(timersRef.current.updateTimer);
    }

    // Delay the update slightly to debounce
    timersRef.current.updateTimer = setTimeout(() => {
      updateFn();
      // Reset flag after a short delay
      setTimeout(() => {
        safeUpdateRef.current = false;
      }, 100);
    }, 50);
  }, []);

  // Clean up any pending timers when component unmounts
  useEffect(() => {
    // Store the current timers reference for cleanup
    const currentTimers = timersRef.current;

    return () => {
      // Use the stored reference in the cleanup function
      Object.values(currentTimers).forEach((timer: any) => {
        if (timer) {
          clearTimeout(timer);
        }
      });
    };
  }, []);
  const checkImagePermission = (profile: IProfileGraphQL) => {
    const use_global_permission =
      // If use own permission is false
      !profile?.use_own_talent_permissions ||
      // if the individual talent permission is null or undefined
      profile?.talent_permissions === null ||
      profile?.talent_permissions === undefined ||
      // If no division means, talent is a non division type
      profile?.division === null ||
      profile?.division === undefined;

    // Permission
    const permissions: string[] =
      (use_global_permission ? profile?.agency?.talent_permissions : profile?.talent_permissions) || [];

    return permissions.includes('Images');
  };

  const agencies = useMemo(() => {
    return (
      profiles?.map((profile) => ({
        current: profile.id === profileId,
        division: profile.division?.name || '',
        id: profile.id,
        name: profile.agency?.full_name || 'Non-Represented',
        disabled: profile.agency?.full_name && !checkImagePermission(profile),
      })) || []
    );
  }, [profiles, profileId]);

  const [selectedAgencies, setSelectedAgencies] = useState(agencies.filter((agency) => !agency.disabled));

  console.log({ selectedAgencies });

  const filteredMedia = useMemo(() => {
    const items = mediaData && mediaProfileData ? mediaData.data : [];
    return items.filter(
      (item) => !mediaProfileData?.data.some((profileItem) => profileItem.attributes.medium_id === item.id),
    );
  }, [mediaData, mediaProfileData]);

  const combinedImages = useMemo(() => {
    const shownImages: any = mediaProfileData && mediaProfileData?.data ? mediaProfileData?.data : [];
    return [...shownImages, ...filteredMedia];
  }, [mediaProfileData, filteredMedia]); // Only re-compute when mediaProfileData or filteredMedia changes

  const { mappedData } = useMediaShareableProfileFormat(combinedImages);

  useEffect(() => {
    if (mediaProfileData) {
      const imageData = mediaProfileData?.data;
      setimages([...imageData]);
    }
  }, [mediaProfileData]);

  useEffect(() => {
    const start = async () => {
      if (skillsData?.data.attributes.skills === null) {
        await updateSkillAsync({
          skills: SkillsData as ISkillCategory[],
        });
        await queryClient.invalidateQueries(['talents/skills']);
      }
    };
    start();
  }, [skillsData, updateSkillAsync, queryClient]);

  const renderPhotos = () => {
    const img = images.map((image: IProfileMedia, idx: number) => {
      return {
        id: image.id,
        src: image.attributes.cropped_attachment || image.attributes.modified_attachment || image.attributes.attachment,
        height: image.attributes.medium_height,
        width: image.attributes.medium_width,
        idx: idx,
      };
    });

    const filImg = filteredMedia.map((image: IMedia, idx: number) => {
      return {
        id: image.id,
        src:
          image.attributes.cropped_attachment_url ||
          image.attributes.modified_attachment_url ||
          image.attributes.attachment_url,
        height: (image.attributes.file_height || 0) as number,
        width: (image.attributes.file_width || 0) as number,
        idx: idx,
      };
    });

    return img.concat(filImg);
  };

  const renderThumbnails = () => {
    const img = images.map((image: IProfileMedia, idx: number) => {
      return {
        id: image.id,
        src: image.attributes.modified_attachment || image.attributes.cropped_attachment || image.attributes.attachment,
        height: image.attributes.medium_height,
        width: image.attributes.medium_width,
        idx: idx,
      };
    });

    const filImg = filteredMedia.map((image: IMedia, idx: number) => {
      return {
        id: image.id,
        src:
          image.attributes.modified_attachment_url ||
          image.attributes.cropped_attachment_url ||
          image.attributes.attachment_url,
        height: (image.attributes.file_height || 0) as number,
        width: (image.attributes.file_width || 0) as number,
        idx: idx,
      };
    });

    return img.concat(filImg);
  };

  const renderedImages = renderPhotos();
  const renderedThumbnails = renderThumbnails();

  const openEditor = useCallback((files: File[]) => {
    setfiles(files);
    setshowEditor(true);
  }, []);

  const handleUnselectMedia = useCallback(
    (profileMediaId: string) => {
      unselectMutate(
        { profileId, profileMediaId },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['profile_media', profileId, { file_type: 'image' }]);
            queryClient.invalidateQueries(['profile', profileId]);
          },
          onError: (errors) => {
            if (errors?.response?.data?.errors) {
              const errorResponseArray: any = errorResponseToArray(errors.response.data.errors);
              console.error('error', errorResponseArray.join(','));
            } else {
              console.error('error', 'Something went wrong');
            }
          },
        },
      );
    },
    [profileId, unselectMutate, queryClient],
  );

  const handleSetSelectMedia = useCallback(
    (payload: IProfileMediaSetSelectPayload) => {
      mutate(
        { profileId, payload: { ...payload, sort: images ? images.length : 0, tag_list: 'image' } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['profile_media', profileId, { file_type: 'image' }]);
            queryClient.invalidateQueries(['profile', profileId]);
          },
          onError: (errors) => {
            if (errors?.response?.data?.errors) {
              const errorResponseArray: any = errorResponseToArray(errors.response.data.errors);
              console.error('error', errorResponseArray.join(','));
            } else {
              console.error('error', 'Something went wrong');
            }
          },
        },
      );
    },
    [images, mutate, profileId, queryClient],
  );

  const handleClick = useCallback((idx: number) => {
    setimageIndex(idx);
    setshowImageModal(true);
  }, []);

  const handleCheck = useCallback(
    (image: any) => {
      handleAgencyTalentPermissions({
        module: 'images',
        callback: () => {
          if (images.find((img) => img.id === image.id)) {
            handleUnselectMedia(image.id);
          } else {
            handleSetSelectMedia({
              id: image.id,
              tag_list: 'image',
            });
          }
        },
        ctaText: 'OK',
      });
    },
    [handleAgencyTalentPermissions, images, handleUnselectMedia, handleSetSelectMedia],
  );

  const onEdit = useCallback(
    (media: any) => {
      setmetaData(media.attributes.modified_metadata || {});
      if (media.attributes.attachment_url) {
        setIdToEdit(media.id);
        const prepFilename = JSON.stringify({
          filename: uuidv4(),
          id: media.id,
        });
        imageURLtoFile(media.attributes.attachment_url, prepFilename).then((file) => {
          openEditor([file]);
        });
      } else {
        const mediumName = isJsonString(media.attributes.medium_name)
          ? JSON.parse(media.attributes.medium_name).filename
          : media.attributes.medium_name;
        const filename = JSON.stringify({
          filename: mediumName,
          id: media.attributes.medium_id,
          isPrimary: media.attributes.primary,
          isCroppedAttachmentNull: media.attributes.cropped_attachment === null,
        });
        setIdToEdit(media.attributes.medium_id);
        imageURLtoFile(media.attributes.attachment, filename).then((file) => {
          openEditor([file]);
        });
      }
    },
    [openEditor],
  );

  // Add dragging state and update handleDragStart, handleDragOver and handleDragEnd
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      // Don't process if already dragging (prevents redundant state updates)
      if (isDragging) return;

      // Check permissions before starting the drag operation
      handleAgencyTalentPermissions({
        module: 'images',
        callback: () => {
          setIsDragging(true);
          setActiveId(event.active.id as string);
        },
        ctaText: 'OK',
      });
    },
    [isDragging, handleAgencyTalentPermissions],
  );

  const handleDragOver = useCallback(
    (event: DragEndEvent) => {
      // Don't process if not in dragging state
      if (!isDragging) return;

      // Clear any existing drag over timer
      if (timersRef.current.dragOverTimer) {
        clearTimeout(timersRef.current.dragOverTimer);
      }

      // Debounce the dragOver handler
      timersRef.current.dragOverTimer = setTimeout(() => {
        // No permission check needed here since we already checked in handleDragStart
        const { active, over } = event;
        if (active && over) {
          if (active?.id !== over?.id) {
            setimages((items: IProfileMedia[]) => {
              const oldIndex = items.findIndex((x: IProfileMedia) => x?.id === active?.id);
              const newIndex = items.findIndex((x: IProfileMedia) => x?.id === over?.id);
              const movedItems = arrayMove(items, oldIndex, newIndex);
              return movedItems;
            });
          }
        }
      }, 50); // shorter delay for drag over since it needs to be more responsive
    },
    [isDragging],
  );

  const sortableImagesRef = useRef<SortableImagesRef>(null);

  const handleDragEnd = useCallback(() => {
    // Don't process if not in dragging state
    if (!isDragging) return;

    // Mark as no longer dragging
    setIsDragging(false);

    // Use safeUpdate to handle the state changes safely
    safeUpdate(() => {
      if (mediaProfileData) {
        const oldItems = mediaProfileData.data;
        const isMediaChanged = !isEqual(oldItems, images);
        if (isMediaChanged) {
          const updatedItems = images.map((item, i) => {
            return {
              id: item.id,
              sort: i,
            };
          });

          // Use another timeout for the mutation
          timersRef.current.updateSortTimer = setTimeout(() => {
            updateSortMutate({ profileId, sort: updatedItems });
          }, 300);

          // Batch these state updates together
          queryClient.setQueriesData(['profile_media', profileId, { file_type: 'image' }], { data: images });
          queryClient.invalidateQueries(['profile', profileId]);
        }
        // Always reset the activeId when drag ends
        setActiveId(null);

        // Reset the SortableImages component state directly
        if (sortableImagesRef.current) {
          sortableImagesRef.current.resetDragState();
        }
      }
    });
  }, [mediaProfileData, images, updateSortMutate, profileId, queryClient, isDragging, safeUpdate]);

  const completion = () => {
    switch (images.length) {
      case 0:
        return 0;
      case 1:
        return 33;
      case 2:
        return 66;
      default:
        return 100;
    }
  };

  const bindToProfile = async (payload: IProfileMediaSetSelectPayload, imgID: string, profileID?: string) => {
    await mutateAsync(
      {
        profileId: profileID || profileId,
        payload: { ...payload, sort: mediaProfileData ? mediaProfileData.data.length : 0 },
      },
      {
        onError: (errors) => {
          if (errors?.response?.data?.errors) {
            const errorResponseArray: any = errorResponseToArray(errors.response.data.errors);
            console.error('error', errorResponseArray.join(','));
          } else {
            console.error('error', 'Something went wrong');
          }
        },
      },
    );
    queryClient.invalidateQueries(['profile_media', profileID || profileId, { file_type: 'image' }]);
    queryClient.invalidateQueries(['media', { file_type: 'image' }]);
    queryClient.invalidateQueries(['profile', profileID || profileId]);
    settoUpload((img) => img.filter((f) => f.id !== imgID));
  };

  const processFiles = (files: File[]) => {
    let isIncorrectFile = false;

    const img = files.map((f) => {
      if (!f.type.includes('image')) {
        isIncorrectFile = true;
        return console.error('File is not an image');
      }
    });

    Promise.all(img).then(() => {
      if (isIncorrectFile) {
        console.error('File is not image');
        handleChangeModalType('error');
        handleSetMessage(
          'Invalid File',
          `${
            files.length > 1
              ? 'One of the selected file is not a valid Image file format.'
              : 'The selected file is not a valid Image file format.'
          }`,
          'OK',
        );
        handleOpen();
      } else {
        const image = files.filter((f) => f.type.includes('image'));
        if (openEditor) {
          setmetaData(null);
          setIdToEdit('');
          setMediaPatching(false);
          openEditor(image);
        }
      }
    });
  };

  const handleOnChange = async (e: any) => {
    const files: File[] = Array.from(e.target.files);
    if (files.length === 0) {
      return;
    }

    processFiles(files);
  };

  const uploadMediaCallback = async (data: any, agencies: any) => {
    try {
      if (data && agencies.length) {
        const { id } = data.data;
        const promises = agencies
          .filter((agency: any) => agency.id !== profileId)
          .map(async (agency: any) => {
            return await bindToProfile(
              {
                id,
                tag_list: 'image',
              },
              id,
              agency.id,
            );
          });
        await Promise.all(promises);
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  return (
    <SectionCont>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        style={{ backgroundColor: 'transparent', margin: 0 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
          <Header>
            <HLeft>
              {width < 960 ? (
                <ColoredImageIcon style={{ width: 30, height: 30, alignSelf: 'center' }} />
              ) : (
                <ColoredImageIcon style={{ width: 40, height: 40, alignSelf: 'center' }} />
              )}

              <HeaderTitle>
                <Name>Images</Name>
                <Count>
                  {images.length ? `${images.length} of ${images.length + filteredMedia.length} used` : 'none'}
                </Count>
              </HeaderTitle>
              {/* {width < 960 && <AddButton onClick={() => setAddModal(true)}>ADD IMAGES</AddButton>} */}
              {width < 960 && (
                <AddButton
                  onClick={(e) => {
                    history.push(`${ROUTES.TALENT.MEDIA}/images`);
                  }}
                >
                  EDIT
                </AddButton>
              )}
              {width < 960 && (
                <AddButton
                  style={{ marginLeft: 10 }}
                  onClick={(e) => {
                    e.stopPropagation();

                    handleAgencyTalentPermissions({
                      module: 'images',
                      callback: () => {
                        if (width < 960) {
                          fileInput.current.click();
                        } else {
                          setAddModal(true);
                        }
                      },
                    });
                  }}
                >
                  ADD
                </AddButton>
              )}
            </HLeft>
            <HRight>
              <CompletionCont>
                <Percent
                  style={{ color: `${completion() === 100 ? '#1fa00f' : completion() > 50 ? '#daa13e' : '#e34343'}` }}
                >
                  {completion()}% Complete
                </Percent>
                <ProgressBar
                  borderRadius={8}
                  percent={completion()}
                  completeColor={`${completion() === 100 ? '#1fa00f' : completion() > 50 ? '#daa13e' : '#e34343'}`}
                  backgroundColor={theme.palette.type === 'dark' ? '#38485d' : 'lightgray'}
                />
              </CompletionCont>
              {width > 960 && (
                <AddButton
                  onClick={(e) => {
                    history.push(`${ROUTES.TALENT.MEDIA}/images`);
                  }}
                >
                  EDIT
                </AddButton>
              )}
              {width > 960 && (
                <>
                  {!permissions?.includes('Images') && permissions && !nonRepre ? (
                    <Tooltip
                      title="Your agency has disabled the ability to edit the media on this profile, use the media manager to upload your files so they are accessible to your agency to include on your profile."
                      placement="top"
                      PopperProps={{ style: { marginBottom: -10 } }}
                    >
                      <div style={{ opacity: 0.5 }}>
                        <AddButton
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          disabled
                        >
                          ADD IMAGES
                        </AddButton>
                      </div>
                    </Tooltip>
                  ) : (
                    <>
                      <AddButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddModal(true);
                        }}
                      >
                        ADD IMAGES
                      </AddButton>
                    </>
                  )}
                </>
              )}
            </HRight>
          </Header>
        </AccordionSummary>
        <AccordionDetails>
          <Content>
            {fetching ? (
              <Fetching>
                <CircularProgress
                  style={{
                    width: 20,
                    height: 20,
                    marginRight: 10,
                  }}
                />{' '}
                Fetching Data...
              </Fetching>
            ) : (
              <>
                {images.length || toUpload.length ? (
                  <SortableImages
                    ref={sortableImagesRef}
                    sensors={sensors}
                    images={images}
                    idToEdit={idToEdit}
                    mediaPatching={mediaPatching}
                    activeId={activeId}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onEdit={onEdit}
                    handleClick={handleClick}
                    handleCheck={handleCheck}
                    handleAgencyTalentPermissions={handleAgencyTalentPermissions}
                  />
                ) : (
                  <Fetching>
                    <Typography style={{ fontSize: 18 }}>No Images Selected</Typography>
                    {filteredMedia.length ? (
                      <Typography style={{ fontSize: 15 }}>
                        You can select from images below or upload a new one.
                      </Typography>
                    ) : (
                      <Typography style={{ fontSize: 15, marginTop: 10 }}>
                        You can upload new images by clicking the 'ADD IMAGES' button.
                      </Typography>
                    )}
                  </Fetching>
                )}
                {toUpload.map((data) => (
                  <UploadingItem key={data.id} type="image" data={data} bindToProfile={bindToProfile} id={data.id} />
                ))}
                {!!filteredMedia.length && (
                  <HiddenCont>
                    <Hidden>Hidden Images</Hidden>

                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {filteredMedia.map((img: IMedia, idx: number) => (
                        <ImageItem
                          idToEdit={idToEdit}
                          isPatching={mediaPatching}
                          edit={() => onEdit(img)}
                          src={img.attributes.modified_attachment_url || img.attributes.attachment_url}
                          img={img}
                          checked={false}
                          check
                          key={img.id}
                          idx={idx + 1 + images.length}
                          onClick={handleClick}
                          checkReturn={handleCheck}
                          handleAgencyTalentPermissions={handleAgencyTalentPermissions}
                        />
                      ))}
                    </div>
                  </HiddenCont>
                )}
              </>
            )}
          </Content>
        </AccordionDetails>
      </Accordion>
      <StyledDialog
        style={{ zIndex: 9999 }}
        open={addModal}
        onClose={() => setAddModal(false)}
        maxWidth="md"
        aria-labelledby="simple-dialog-title"
      >
        <AddModal
          close={() => setAddModal(false)}
          type="Images"
          openEditor={(file) => {
            setmetaData(null);
            openEditor(file);
          }}
        />
      </StyledDialog>
      {showEditor && files && mediaTags && !fetchingTags && mediaData && !isMediaLoading && skillsData && (
        <>
          <AsyncPhotoEditorV2
            selectedAgencies={selectedAgencies}
            setSelectedAgencies={setSelectedAgencies}
            agencies={agencies}
            images={files}
            mediaTags={mediaTags.data}
            variant="default"
            {...{
              patchMultipleMediaAsync,
              patchMediaTagsAsync,
              uploadMediaMutateAsync,
              setSelectProfileMediaMutateAsync,
              updateSkillAsync,
              uploadMediaCallback,
            }}
            featureEnableRenderSkillTags={true}
            enableUndo={true}
            skillsData={skillsData.data}
            profileId={profileId}
            mediaData={mediaData.data}
            show={showEditor}
            close={async () => {
              document.body.style.overflow = 'auto';
              setTimeout(() => {
                document.body.style.overflow = 'visible';
              }, 100);
              setshowEditor(false);
              setMediaPatching(false);
              setIdToEdit('');
              await queryClient.invalidateQueries(['profile_media', profileId, { file_type: 'image' }]);
              await queryClient.invalidateQueries(['media', { file_type: 'image' }]);
            }}
            onSuccessImageUpdate={async (data: any) => {
              await queryClient.invalidateQueries(['talents/skills']);
              await queryClient.resetQueries(['profile_primary_image', profileId]);

              if (files.length === 1) {
                if (isJsonString(files[0].name)) {
                  const { isCroppedAttachmentNull, isPrimary } = JSON.parse(files[0].name);
                  if (isCroppedAttachmentNull) {
                    const { id } = JSON.parse(files[0].name);
                    unselectMutate(
                      { profileId: profileId, profileMediaId: id },
                      {
                        onSuccess: () => {
                          queryClient.invalidateQueries(['profile_media', profileId, { file_type: 'image' }]);
                          queryClient.invalidateQueries(['profile', profileId]);
                        },
                        onError: (errors) => {
                          if (errors?.response?.data?.errors) {
                            const errorResponseArray: any = errorResponseToArray(errors.response.data.errors);
                            console.error('error', errorResponseArray.join(','));
                          } else {
                            console.error('error', 'Something went wrong');
                          }
                        },
                      },
                    );
                  }
                  if (isPrimary) {
                    setSelectProfileMediaMutateAsync(
                      {
                        profileId: profileId,
                        payload: {
                          ...{
                            id: data.data.id,
                            tag_list: 'image',
                          },
                          sort: images ? images.length : 0,
                          tag_list: 'image',
                        },
                      },
                      {
                        onSuccess: async () => {
                          queryClient.resetQueries(['profile_primary_image', profileId]);
                          const formData = new FormData();
                          formData.set('medium_id', data.data.id);
                          primaryImageMutate(
                            { profileId: profileId, formData },
                            {
                              onSuccess: async () => {
                                await queryClient.resetQueries(['profile_primary_image', profileId]);
                              },
                            },
                          );
                        },
                        onError: (errors) => {
                          queryClient.resetQueries(['profile_primary_image', profileId]);
                          console.error('error', errors);
                        },
                      },
                    );
                  }
                }
              }
            }}
            onSuccessImageUpload={async (data: any) => {
              document.body.style.overflow = 'auto';
              await queryClient.invalidateQueries(['talents/skills']);
              await queryClient.resetQueries(['profile_primary_image', profileId]);
            }}
          />
        </>
      )}
      {/* <ImageGallery
        fullScreen={width < 960}
        current={imageIndex - 1}
        images={renderedImages}
        thumbnails={renderedThumbnails}
        open={showImageModal}
        onClose={() => setshowImageModal(false)}
      /> */}
      {showImageModal ? (
        <AsyncMediaGallery
          data={mappedData || []}
          selectedIndex={imageIndex - 1}
          type={'image'}
          openGallery={showImageModal}
          handleOpenGallery={() => setshowImageModal(false)}
          sharedTheme={theme.palette.type}
        />
      ) : (
        <Fragment />
      )}
      <input multiple accept="image/*" type="file" ref={fileInput} hidden onChange={handleOnChange} />
    </SectionCont>
  );
}

export default Images;
